import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { api } from '../../../services/api';

// ========================================
// TYPE DEFINITIONS
// ========================================

export type AnalysisType = 'instant' | 'correlation' | 'differential' | 'grn' | 'stage';
export type PresetType = 'quick' | 'standard' | 'sensitive' | 'custom';
export type ResultTab = 'overview' | 'table' | 'plots' | 'network' | 'knockdown' | 'export';

export interface CellTypeSelection {
  disease: string[];
  normal: string[];
}

export interface CorrelationParameters {
  max_cells: number;
  n_clusters: number;
  top_n_lncrnas: number;
  n_genes: number;
  enrichment_threshold: number;
}

export interface DifferentialParameters {
  min_log_fc: number;
  max_pvalue: number;
}

export interface GRNParameters {
  correlation_threshold: number;
  max_neighbors: number;
}

export interface StageParameters {
  min_log_fc: number;
  max_pvalue: number;
}

export interface AnalysisParameters {
  correlation?: CorrelationParameters;
  differential?: DifferentialParameters;
  grn?: GRNParameters;
  stage?: StageParameters;
}

export interface DiseaseMetadata {
  availableCellTypes: number;
  totalSamples: number;
}

export interface Dataset {
  id: string;
  disease: string;
  n_disease_cells: number;
  n_normal_cells: number;
  total_cells: number;
  collection_date?: string;
  source?: string;
}

export interface AnalysisWorkflowState {
  // Step tracking
  currentStep: 1 | 2 | 3 | 4 | 5;
  completedSteps: number[];

  // Step 1: Disease Selection
  selectedDisease: string | null;
  diseaseMetadata: DiseaseMetadata | null;

  // Step 1.5: Dataset Selection
  selectedDatasetId: string | null;
  availableDatasets: Dataset[];
  loadingDatasets: boolean;

  // Step 2: Cell Type Selection
  selectedCellTypes: CellTypeSelection;

  // Step 3: Analysis Configuration
  selectedAnalysisType: AnalysisType;
  parameters: AnalysisParameters;
  preset: PresetType;

  // Step 4: Results
  analysisResult: any | null;
  isAnalyzing: boolean;
  analysisError: Error | null;
  activeResultTab: ResultTab;

  // Refinement mode
  isRefinementMode: boolean;
  previousResult: any | null;
  retryCount: number;

  // Session management
  sessionId: string;
  lastExportedAt: Date | null;
}

export interface AnalysisContextValue extends AnalysisWorkflowState {
  // Navigation actions
  setCurrentStep: (step: 1 | 2 | 3 | 4 | 5) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  canNavigateToStep: (step: number) => boolean;

  // Step 1 actions
  setSelectedDisease: (disease: string, metadata?: DiseaseMetadata) => void;

  // Step 1.5 actions
  fetchDatasetsForDisease: (disease: string) => Promise<void>;
  setSelectedDatasetId: (id: string | null) => void;

  // Step 2 actions
  toggleCellType: (cellType: string, condition: 'disease' | 'normal') => void;
  setCellTypes: (cellTypes: CellTypeSelection) => void;
  clearCellTypes: (condition?: 'disease' | 'normal') => void;

  // Step 3 actions
  setAnalysisType: (type: AnalysisType) => void;
  updateParameter: (
    analysisType: AnalysisType,
    parameter: string,
    value: any
  ) => void;
  setPreset: (preset: PresetType) => void;
  applyPresetParameters: (preset: PresetType) => void;

  // Step 4 actions
  runAnalysis: () => Promise<void>;
  setAnalysisResult: (result: any) => void;
  setAnalysisError: (error: Error | null) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  setActiveResultTab: (tab: ResultTab) => void;

  // Refinement mode actions
  enterRefinementMode: () => void;
  exitRefinementMode: () => void;
  applySuggestedParameters: (params: Partial<AnalysisParameters>) => void;

  // Session management
  exportSession: () => any;
  importSession: (session: any) => void;

  // Workflow actions
  resetWorkflow: () => void;
  validateCurrentStep: () => { valid: boolean; errors: string[] };
}

// ========================================
// DEFAULT VALUES
// ========================================

const DEFAULT_CORRELATION_PARAMS: CorrelationParameters = {
  max_cells: 20000,
  n_clusters: 5,
  top_n_lncrnas: 1000,
  n_genes: 1000,
  enrichment_threshold: 1.5,
};

const DEFAULT_DIFFERENTIAL_PARAMS: DifferentialParameters = {
  min_log_fc: 1.5,
  max_pvalue: 0.05,
};

const DEFAULT_GRN_PARAMS: GRNParameters = {
  correlation_threshold: 0.25,
  max_neighbors: 15,
};

const DEFAULT_STAGE_PARAMS: StageParameters = {
  min_log_fc: 1.5,
  max_pvalue: 0.05,
};

const INITIAL_STATE: AnalysisWorkflowState = {
  currentStep: 1,
  completedSteps: [],
  selectedDisease: null,
  diseaseMetadata: null,
  selectedDatasetId: null,
  availableDatasets: [],
  loadingDatasets: false,
  selectedCellTypes: {
    disease: [],
    normal: [],
  },
  selectedAnalysisType: 'instant',
  parameters: {
    correlation: DEFAULT_CORRELATION_PARAMS,
    differential: DEFAULT_DIFFERENTIAL_PARAMS,
    grn: DEFAULT_GRN_PARAMS,
    stage: DEFAULT_STAGE_PARAMS,
  },
  preset: 'standard',
  analysisResult: null,
  isAnalyzing: false,
  analysisError: null,
  activeResultTab: 'overview',
  isRefinementMode: false,
  previousResult: null,
  retryCount: 0,
  sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  lastExportedAt: null,
};

// ========================================
// PRESET CONFIGURATIONS
// ========================================

const PRESET_CONFIGS: Record<PresetType, Partial<CorrelationParameters>> = {
  quick: {
    max_cells: 100,
    n_clusters: 3,
    top_n_lncrnas: 15,
    n_genes: 200,
  },
  standard: {
    max_cells: 20000,
    n_clusters: 5,
    top_n_lncrnas: 1000,
    n_genes: 1000,
  },
  sensitive: {
    max_cells: 1000,
    n_clusters: 8,
    top_n_lncrnas: 50,
    n_genes: 1000,
  },
  custom: {},
};

// ========================================
// CONTEXT CREATION
// ========================================

const AnalysisContext = createContext<AnalysisContextValue | null>(null);

// ========================================
// PROVIDER COMPONENT
// ========================================

export const AnalysisContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AnalysisWorkflowState>(INITIAL_STATE);

  // ========================================
  // URL STATE SYNC
  // ========================================

  // Load state from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const step = searchParams.get('step');
    const disease = searchParams.get('disease');
    const datasetId = searchParams.get('dataset_id');
    const cells = searchParams.get('cells');
    const analysis = searchParams.get('analysis');
    const params = searchParams.get('params');
    const refining = searchParams.get('refining');

    if (step || disease || cells || analysis) {
      setState((prev) => {
        const updates: Partial<AnalysisWorkflowState> = {};

        if (step) {
          const stepNum = parseInt(step) as 1 | 2 | 3 | 4;
          if (stepNum >= 1 && stepNum <= 4) {
            updates.currentStep = stepNum;
            updates.completedSteps = Array.from({ length: stepNum - 1 }, (_, i) => i + 1);
          }
        }

        if (disease) {
          updates.selectedDisease = decodeURIComponent(disease);
        }

        if (datasetId) {
          updates.selectedDatasetId = decodeURIComponent(datasetId);
        }

        if (cells) {
          try {
            const cellList = cells.split(',').map((c) => decodeURIComponent(c));
            // Assume disease cells by default if not specified
            updates.selectedCellTypes = {
              disease: cellList,
              normal: [],
            };
          } catch (e) {
            console.error('Failed to parse cells from URL:', e);
          }
        }

        if (analysis && ['correlation', 'differential', 'grn', 'stage'].includes(analysis)) {
          updates.selectedAnalysisType = analysis as AnalysisType;
        }

        if (params) {
          try {
            const decoded = JSON.parse(atob(params));
            updates.parameters = { ...prev.parameters, ...decoded };
          } catch (e) {
            console.error('Failed to parse parameters from URL:', e);
          }
        }

        if (refining === '1') {
          updates.isRefinementMode = true;
        }

        return { ...prev, ...updates };
      });
    }
  }, []); // Run only on mount

  // Update URL when state changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams();

      if (state.currentStep !== 1) {
        params.set('step', state.currentStep.toString());
      }

      if (state.selectedDisease) {
        params.set('disease', encodeURIComponent(state.selectedDisease));
      }

      if (state.selectedDatasetId) {
        params.set('dataset_id', encodeURIComponent(state.selectedDatasetId));
      }

      const allCells = [
        ...state.selectedCellTypes.disease,
        ...state.selectedCellTypes.normal,
      ];
      if (allCells.length > 0) {
        params.set('cells', allCells.map((c) => encodeURIComponent(c)).join(','));
      }

      if (state.selectedAnalysisType !== 'correlation') {
        params.set('analysis', state.selectedAnalysisType);
      }

      if (state.preset === 'custom') {
        try {
          const paramJson = JSON.stringify(state.parameters);
          params.set('params', btoa(paramJson));
        } catch (e) {
          console.error('Failed to encode parameters:', e);
        }
      }

      if (state.isRefinementMode) {
        params.set('refining', '1');
      }

      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }, 500); // Debounce 500ms

    return () => clearTimeout(timer);
  }, [
    state.currentStep,
    state.selectedDisease,
    state.selectedDatasetId,
    state.selectedCellTypes,
    state.selectedAnalysisType,
    state.preset,
    state.parameters,
    state.isRefinementMode,
  ]);

  // ========================================
  // VALIDATION
  // ========================================

  const validateCurrentStep = useCallback((): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const showDatasetStep = state.availableDatasets.length > 1;

    switch (state.currentStep) {
      case 1: // Disease Selection
        if (!state.selectedDisease) {
          errors.push('Please select a disease');
        }
        break;

      case 2: // Dataset Selection (if shown) OR Cell Type Selection (if not shown)
        if (showDatasetStep) {
          // Step 2 is dataset selection when multiple datasets exist
          if (!state.selectedDatasetId) {
            errors.push('Please select a dataset');
          }
        } else {
          // Step 2 is cell type selection when no dataset step
          const totalCells =
            state.selectedCellTypes.disease.length + state.selectedCellTypes.normal.length;
          if (totalCells === 0) {
            errors.push('Please select at least one cell type');
          }
        }
        break;

      case 3: // Cell Type Selection (if dataset shown) OR Analysis Configuration (if not shown)
        if (showDatasetStep) {
          // Step 3 is cell type selection when dataset step is shown
          const totalCells =
            state.selectedCellTypes.disease.length + state.selectedCellTypes.normal.length;
          if (totalCells === 0) {
            errors.push('Please select at least one cell type');
          }
        } else {
          // Step 3 is analysis configuration when no dataset step
          if (state.selectedAnalysisType === 'correlation') {
            const params = state.parameters.correlation!;
            if (params.max_cells < 100 || params.max_cells > 20000) {
              errors.push('Max cells must be between 100 and 20,000');
            }
            if (params.n_clusters < 2 || params.n_clusters > 20) {
              errors.push('Number of clusters must be between 2 and 20');
            }
          }
        }
        break;

      case 4: // Analysis Configuration (if dataset shown) OR Results (if not shown)
        if (showDatasetStep) {
          // Step 4 is analysis configuration when dataset step is shown
          if (state.selectedAnalysisType === 'correlation') {
            const params = state.parameters.correlation!;
            if (params.max_cells < 100 || params.max_cells > 20000) {
              errors.push('Max cells must be between 100 and 20,000');
            }
            if (params.n_clusters < 2 || params.n_clusters > 20) {
              errors.push('Number of clusters must be between 2 and 20');
            }
          }
        }
        // Step 4 is results when no dataset step - always valid
        break;

      case 5: // Results (only when dataset step is shown)
        // Always valid (results display)
        break;
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }, [state]);

  // ========================================
  // NAVIGATION ACTIONS
  // ========================================

  const canNavigateToStep = useCallback((step: number): boolean => {
    // Can always navigate to step 1
    if (step === 1) return true;

    // Can navigate to completed steps
    if (state.completedSteps.includes(step)) return true;

    // Can navigate to next step if current step is completed
    if (step === state.currentStep + 1) {
      const validation = validateCurrentStep();
      return validation.valid;
    }

    // Cannot skip ahead
    return false;
  }, [state.completedSteps, state.currentStep, validateCurrentStep]);

  const setCurrentStep = useCallback((step: 1 | 2 | 3 | 4 | 5) => {
    if (canNavigateToStep(step)) {
      setState((prev) => ({
        ...prev,
        currentStep: step,
      }));
    }
  }, [canNavigateToStep]);

  const goToNextStep = useCallback(() => {
    console.log('[AnalysisContext] goToNextStep called, current state:', {
      currentStep: state.currentStep,
      selectedDisease: state.selectedDisease,
      availableDatasets: state.availableDatasets.length,
    });

    const validation = validateCurrentStep();
    console.log('[AnalysisContext] Validation result:', validation);

    if (!validation.valid) {
      console.error('Cannot proceed:', validation.errors);
      return;
    }

    setState((prev) => {
      // Determine max step based on whether dataset selection is shown
      const maxStep = prev.availableDatasets.length > 1 ? 5 : 4;
      const nextStep = Math.min(prev.currentStep + 1, maxStep) as 1 | 2 | 3 | 4 | 5;
      const updatedSteps = prev.completedSteps.includes(prev.currentStep)
        ? prev.completedSteps
        : [...prev.completedSteps, prev.currentStep];

      console.log('[AnalysisContext] Moving to next step:', {
        from: prev.currentStep,
        to: nextStep,
        maxStep,
      });

      return {
        ...prev,
        currentStep: nextStep,
        completedSteps: updatedSteps,
      };
    });
  }, [state.currentStep, state.selectedDisease, state.availableDatasets.length, validateCurrentStep]);

  const goToPreviousStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1) as 1 | 2 | 3 | 4 | 5,
    }));
  }, []);

  // ========================================
  // STEP 1 ACTIONS
  // ========================================

  const setSelectedDisease = useCallback(
    (disease: string, metadata?: DiseaseMetadata) => {
      setState((prev) => ({
        ...prev,
        selectedDisease: disease,
        diseaseMetadata: metadata || null,
        // Reset downstream state
        selectedDatasetId: null,
        availableDatasets: [],
        selectedCellTypes: { disease: [], normal: [] },
        analysisResult: null,
      }));
    },
    []
  );

  // ========================================
  // STEP 1.5 ACTIONS (Dataset Selection)
  // ========================================

  const fetchDatasetsForDisease = useCallback(async (disease: string) => {
    setState((prev) => ({ ...prev, loadingDatasets: true }));

    try {
      const datasets: Dataset[] = await api.getDatasetsForDisease(disease);

      setState((prev) => ({
        ...prev,
        availableDatasets: datasets,
        loadingDatasets: false,
        // Auto-select if only one dataset
        selectedDatasetId: datasets.length === 1 ? datasets[0].id : null,
      }));
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
      setState((prev) => ({
        ...prev,
        availableDatasets: [],
        loadingDatasets: false,
      }));
    }
  }, []);

  const setSelectedDatasetId = useCallback((id: string | null) => {
    setState((prev) => ({
      ...prev,
      selectedDatasetId: id,
    }));
  }, []);

  // ========================================
  // STEP 2 ACTIONS
  // ========================================

  const toggleCellType = useCallback(
    (cellType: string, condition: 'disease' | 'normal') => {
      setState((prev) => {
        const list = prev.selectedCellTypes[condition];
        const newList = list.includes(cellType)
          ? list.filter((c) => c !== cellType)
          : [...list, cellType];

        return {
          ...prev,
          selectedCellTypes: {
            ...prev.selectedCellTypes,
            [condition]: newList,
          },
        };
      });
    },
    []
  );

  const setCellTypes = useCallback((cellTypes: CellTypeSelection) => {
    setState((prev) => ({
      ...prev,
      selectedCellTypes: cellTypes,
    }));
  }, []);

  const clearCellTypes = useCallback((condition?: 'disease' | 'normal') => {
    setState((prev) => {
      if (condition) {
        return {
          ...prev,
          selectedCellTypes: {
            ...prev.selectedCellTypes,
            [condition]: [],
          },
        };
      } else {
        return {
          ...prev,
          selectedCellTypes: { disease: [], normal: [] },
        };
      }
    });
  }, []);

  // ========================================
  // STEP 3 ACTIONS
  // ========================================

  const setAnalysisType = useCallback((type: AnalysisType) => {
    setState((prev) => ({
      ...prev,
      selectedAnalysisType: type,
    }));
  }, []);

  const updateParameter = useCallback(
    (analysisType: AnalysisType, parameter: string, value: any) => {
      setState((prev) => ({
        ...prev,
        parameters: {
          ...prev.parameters,
          [analysisType]: {
            ...prev.parameters[analysisType],
            [parameter]: value,
          },
        },
        preset: 'custom', // Mark as custom when manually changed
      }));
    },
    []
  );

  const setPreset = useCallback((preset: PresetType) => {
    setState((prev) => ({
      ...prev,
      preset,
    }));
  }, []);

  const applyPresetParameters = useCallback((preset: PresetType) => {
    if (preset === 'custom') return;

    setState((prev) => ({
      ...prev,
      preset,
      parameters: {
        ...prev.parameters,
        correlation: {
          ...prev.parameters.correlation!,
          ...PRESET_CONFIGS[preset],
        },
      },
    }));
  }, []);

  // ========================================
  // STEP 4 ACTIONS
  // ========================================

  const runAnalysis = useCallback(async () => {
    if (!state.selectedDisease || state.selectedCellTypes.disease.length === 0) {
      console.error('Cannot run analysis: missing disease or cell types');
      return;
    }

    setState((prev) => ({
      ...prev,
      isAnalyzing: true,
      analysisError: null,
    }));

    try {
      let result;
      // Combine and deduplicate cell types (same cell type might exist in both disease and normal)
      const allCellTypes = Array.from(new Set([
        ...state.selectedCellTypes.disease,
        ...state.selectedCellTypes.normal,
      ]));

      switch (state.selectedAnalysisType) {
        case 'instant': {
          // Use dual ranking for instant target discovery (combines expression + network priority)
          const startTime = Date.now();

          try {
            // Try dual ranking endpoint first
            const dualRankingResult = await api.getDualRankings({
              disease: state.selectedDisease,
              dataset_id: state.selectedDatasetId || undefined,
              cell_types: allCellTypes.length > 0 ? allCellTypes : undefined,
              top_n: 100,
              max_adj_pvalue: 0.05,
            });

            // Transform dual ranking response to match expected analysis result format
            result = {
              // Keep targets array for dual ranking UI components
              targets: dualRankingResult.targets,

              // Also provide lncrna_results for backward compatibility with single-rank views
              lncrna_results: dualRankingResult.targets.map((target: any, index: number) => ({
                lncrna: target.lncrna,
                rank: target.expression_rank,
                in_grn: target.has_grn || false,
                max_abs_log2fc: Math.abs(target.log2fc),
                min_adj_pvalue: target.adj_pvalue,
                n_cell_types: target.cell_type_stats?.length || 0,
              })),

              // Summary metrics from dual ranking
              n_consensus: dualRankingResult.n_consensus,
              n_top_expression: dualRankingResult.n_top_expression,
              n_top_network: dualRankingResult.n_top_network,
              n_divergent: dualRankingResult.n_divergent,
              n_moderate: dualRankingResult.n_moderate,

              // Cell counts (from first target if available)
              n_disease_cells: dualRankingResult.targets[0]?.cell_type_stats?.[0]?.disease_n_cells || 0,
              n_normal_cells: dualRankingResult.targets[0]?.cell_type_stats?.[0]?.normal_n_cells || 0,

              // Metadata
              execution_time: (Date.now() - startTime) / 1000,
              analysis_type: 'instant',
              dataset_id: dualRankingResult.dataset_id,
              query_time_ms: dualRankingResult.query_time_ms,
            };
          } catch (error) {
            console.warn('Dual ranking failed, falling back to expression stats:', error);

            // Fallback to expression stats if dual ranking fails
            const expressionStatsResult = await api.getExpressionStats({
              disease: state.selectedDisease,
              dataset_id: state.selectedDatasetId || undefined,
              cell_types: allCellTypes.length > 0 ? allCellTypes : undefined,
              top_n: 100,
              max_adj_pvalue: 0.05,
            });

            // Transform expression stats response (single-rank only)
            result = {
              lncrna_results: expressionStatsResult.lncrnas.map((lnc: any, index: number) => ({
                lncrna: lnc.lncrna,
                rank: index + 1,
                in_grn: lnc.has_grn || false,
                max_abs_log2fc: Math.max(...lnc.cell_type_stats.map((s: any) => Math.abs(s.log2fc))),
                min_adj_pvalue: Math.min(...lnc.cell_type_stats.map((s: any) => s.adj_pvalue)),
                n_cell_types: lnc.cell_type_stats.length,
              })),
              n_disease_cells: expressionStatsResult.lncrnas[0]?.cell_type_stats[0]?.disease_n_cells || 0,
              n_normal_cells: expressionStatsResult.lncrnas[0]?.cell_type_stats[0]?.normal_n_cells || 0,
              execution_time: (Date.now() - startTime) / 1000,
              analysis_type: 'instant',
              dataset_id: expressionStatsResult.dataset_id,
              n_cell_types: expressionStatsResult.n_cell_types,
              query_time_ms: expressionStatsResult.query_time_ms,
            };
          }
          break;
        }

        case 'correlation': {
          const params = state.parameters.correlation!;
          result = await api.analyzeCorrelation({
            disease: state.selectedDisease,
            cell_types: allCellTypes,
            max_cells: params.max_cells,
            n_genes: params.n_genes,
            top_n_lncrnas: params.top_n_lncrnas,
            n_clusters: params.n_clusters,
            dataset_id: state.selectedDatasetId || undefined,
            use_prioritization: true,
          });
          break;
        }

        case 'differential': {
          const params = state.parameters.differential!;
          result = await api.analyzeSpecificity({
            disease: state.selectedDisease,
            disease_cell_types: state.selectedCellTypes.disease,
            control_cell_types: state.selectedCellTypes.normal,
            min_log_fc: params.min_log_fc,
            max_pvalue: params.max_pvalue,
            dataset_id: state.selectedDatasetId || undefined,
          });
          break;
        }

        case 'grn': {
          // First get expression data (will load from GCP if dataset_id provided)
          const expressionData = await api.getExpressionForGRN(
            state.selectedDisease,
            state.selectedDatasetId || undefined
          );

          const params = state.parameters.grn!;
          result = await api.buildGRN({
            gene_list: expressionData.gene_symbols || [],
            disease: state.selectedDisease,
            correlation_threshold: params.correlation_threshold,
            max_neighbors: params.max_neighbors,
            expression_matrix: expressionData.expression_matrix,
            gene_symbols: expressionData.gene_symbols,
            use_expression_correlation: true,
            dataset_id: state.selectedDatasetId || undefined,
          });
          break;
        }

        case 'stage': {
          // Stage analysis not fully implemented yet - return mock data
          console.warn('Stage analysis not yet implemented, returning empty results');
          result = {
            lncrna_results: [],
            n_disease_cells: 0,
            n_normal_cells: 0,
            execution_time: 0,
            analysis_type: 'stage',
            dataset_id: state.selectedDatasetId,
            parameters: state.parameters.stage,
          };
          break;
        }

        default:
          throw new Error(`Unknown analysis type: ${state.selectedAnalysisType}`);
      }

      setState((prev) => ({
        ...prev,
        analysisResult: result,
        isAnalyzing: false,
        analysisError: null,
        retryCount: 0,
        lastExportedAt: null,
      }));
    } catch (error: any) {
      console.error('Analysis failed:', error);
      setState((prev) => ({
        ...prev,
        analysisError: error,
        isAnalyzing: false,
        retryCount: prev.retryCount + 1,
      }));
    }
  }, [
    state.selectedDisease,
    state.selectedCellTypes,
    state.selectedAnalysisType,
    state.parameters,
    state.selectedDatasetId,
  ]);

  const setAnalysisResult = useCallback((result: any) => {
    setState((prev) => ({
      ...prev,
      analysisResult: result,
      isAnalyzing: false,
      analysisError: null,
      retryCount: 0, // Reset retry count on success
    }));
  }, []);

  const setAnalysisError = useCallback((error: Error | null) => {
    setState((prev) => ({
      ...prev,
      analysisError: error,
      isAnalyzing: false,
      retryCount: prev.retryCount + 1,
    }));
  }, []);

  const setIsAnalyzing = useCallback((isAnalyzing: boolean) => {
    setState((prev) => ({
      ...prev,
      isAnalyzing,
    }));
  }, []);

  const setActiveResultTab = useCallback((tab: ResultTab) => {
    setState((prev) => ({
      ...prev,
      activeResultTab: tab,
    }));
  }, []);

  // ========================================
  // REFINEMENT MODE ACTIONS
  // ========================================

  const enterRefinementMode = useCallback(() => {
    setState((prev) => {
      // Determine the correct step for analysis configuration
      // If dataset step is shown (multiple datasets), analysis is step 4
      // Otherwise, analysis is step 3
      const analysisConfigStep = (prev.availableDatasets.length > 1 ? 4 : 3) as 1 | 2 | 3 | 4 | 5;

      return {
        ...prev,
        isRefinementMode: true,
        previousResult: prev.analysisResult,
        currentStep: analysisConfigStep,
      };
    });
  }, []);

  const exitRefinementMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isRefinementMode: false,
      previousResult: null,
      retryCount: 0,
    }));
  }, []);

  const applySuggestedParameters = useCallback((params: Partial<AnalysisParameters>) => {
    setState((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        ...params,
      },
      preset: 'custom',
      isRefinementMode: true,
      currentStep: 3, // Navigate to Step 3 to review
    }));
  }, []);

  // ========================================
  // SESSION MANAGEMENT
  // ========================================

  const exportSession = useCallback(() => {
    return {
      sessionId: state.sessionId,
      timestamp: new Date().toISOString(),
      state: {
        selectedDisease: state.selectedDisease,
        selectedDatasetId: state.selectedDatasetId,
        selectedCellTypes: state.selectedCellTypes,
        selectedAnalysisType: state.selectedAnalysisType,
        parameters: state.parameters,
        preset: state.preset,
        analysisResult: state.analysisResult,
      },
    };
  }, [state]);

  const importSession = useCallback((session: any) => {
    if (!session || !session.state) {
      console.error('Invalid session data');
      return;
    }

    setState((prev) => ({
      ...prev,
      ...session.state,
      sessionId: session.sessionId || prev.sessionId,
      currentStep: 4, // Navigate to results if analysis result exists
      completedSteps: [1, 2, 3],
    }));
  }, []);

  // ========================================
  // WORKFLOW ACTIONS
  // ========================================

  const resetWorkflow = useCallback(() => {
    setState(INITIAL_STATE);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // ========================================
  // CONTEXT VALUE
  // ========================================

  const value: AnalysisContextValue = {
    ...state,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    canNavigateToStep,
    setSelectedDisease,
    fetchDatasetsForDisease,
    setSelectedDatasetId,
    toggleCellType,
    setCellTypes,
    clearCellTypes,
    setAnalysisType,
    updateParameter,
    setPreset,
    applyPresetParameters,
    runAnalysis,
    setAnalysisResult,
    setAnalysisError,
    setIsAnalyzing,
    setActiveResultTab,
    enterRefinementMode,
    exitRefinementMode,
    applySuggestedParameters,
    exportSession,
    importSession,
    resetWorkflow,
    validateCurrentStep,
  };

  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
};

// ========================================
// HOOK
// ========================================

export const useAnalysisContext = (): AnalysisContextValue => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysisContext must be used within AnalysisContextProvider');
  }
  return context;
};

export default AnalysisContext;
